/**
 * Utility functions for formatting and displaying data in the Devis components
 */

import React from 'react';

/**
 * Format quantities for display, showing either a simple number or an expandable
 * list of size/quantity pairs
 * 
 * @param {Object} quote - The quote object containing quantity information
 * @param {Object} expandedQuantities - Object tracking which quotes have expanded quantity displays
 * @param {Function} toggleQuantityExpansion - Function to toggle expansion state
 * @returns {JSX.Element} - Formatted quantity display
 */
export const formatQuantities = (quote, expandedQuantities, toggleQuantityExpansion) => {
  const hasSizesQuantities = quote.sizes_quantities && Object.keys(quote.sizes_quantities).length > 0;

  let total = 0;
  if (hasSizesQuantities) {
    total = Object.values(quote.sizes_quantities).reduce((sum, qty) => sum + parseInt(qty || 0), 0);
  } else {
    total = parseInt(quote.quantity || 0);
  }

  return (
    <div className="relative">
      <div
        className="flex items-center cursor-pointer"
        onClick={() => toggleQuantityExpansion(quote.id)}
      >
        <span className="mr-1">{total}</span>
        {hasSizesQuantities && (
          <span className="text-xs bg-gray-200 rounded-full px-2 py-0.5 text-gray-700">
            {expandedQuantities[quote.id] ? "▲" : "▼"}
          </span>
        )}
      </div>

      {hasSizesQuantities && expandedQuantities[quote.id] && (
        <div className="absolute z-10 bg-white shadow-md rounded-md mt-1 py-2 px-3 text-sm border border-gray-200 w-36 -left-10">
          <div className="space-y-1">
            {Object.entries(quote.sizes_quantities).map(([size, quantity]) => (
              <div key={`${quote.id}-${size}`} className="flex justify-between">
                <span className="font-medium">{size}:</span>
                <span>{quantity}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Format product colors for display
 * 
 * @param {Object} quote - The quote object containing product color information
 * @returns {JSX.Element|string} - Formatted color display or "Non spécifié" message
 */
export const formatProductColors = (quote) => {
  if (Array.isArray(quote.product_colors)) {
    return (
      <div className="flex flex-wrap gap-1">
        {quote.product_colors.map((color, index) => {
          const cleanColor = typeof color === "string" ? color.trim() : "";

          const handleCopyColor = () => {
            navigator.clipboard
              .writeText(cleanColor)
              .then(() => {
                alert(`${cleanColor} copié !`);
              });
          };

          return (
            <div
              key={`${quote.id}-color-${index}`}
              className="w-12 h-6 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer"
              title={`Cliquer pour copier: ${cleanColor}`}
              style={{
                backgroundColor: cleanColor,
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }}
              onClick={handleCopyColor}
            />
          );
        })}
      </div>
    );
  } else if (typeof quote.product_colors === "string") {
    return (
      <div className="flex flex-wrap gap-1">
        {quote.product_colors
          .split(",")
          .map((color, index) => {
            const cleanColor = color.trim();

            const handleCopyColor = () => {
              navigator.clipboard
                .writeText(cleanColor)
                .then(() => {
                  alert(`${cleanColor} copié !`);
                });
            };

            return (
              <div
                key={`${quote.id}-color-${index}`}
                className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer"
                title={`Cliquer pour copier: ${cleanColor}`}
                style={{
                  backgroundColor: cleanColor,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                }}
                onClick={handleCopyColor}
              />
            );
          })}
      </div>
    );
  } else {
    return <span className="text-gray-400">Non spécifié</span>;
  }
};